import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RunListQuery, useApi } from "../api/api";
import {
  AlertBanner,
  PageTitle,
  Panel,
  ResourceLink,
  StatusChip,
  Td,
  Th,
  btnSecondary,
  inputClass,
  selectClass,
} from "../api/controlPlaneUi";
import { Run, RunStatus } from "../api/types";

const PAGE_SIZE = 20;

const STATUSES: RunStatus[] = [
  "pending",
  "running",
  "needs_rework",
  "completed",
  "failed",
];

const SORT_COLUMNS: { key: RunListQuery["sort"]; label: string }[] = [
  { key: "createdAt", label: "Created" },
  { key: "title", label: "Name" },
  { key: "storySlug", label: "Story" },
  { key: "status", label: "Status" },
  { key: "iterations", label: "Iterations" },
  { key: "completedAt", label: "Completed" },
];

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const shortId = (id: string): string =>
  id.length > 8 ? `${id.slice(0, 8)}…` : id;

const RunsPage: React.FC = () => {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const sort = (searchParams.get("sort") as RunListQuery["sort"]) || "createdAt";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";
  const status = searchParams.get("status") ?? "";
  const storySlug = searchParams.get("storySlug") ?? "";
  const search = searchParams.get("search") ?? "";

  const [searchInput, setSearchInput] = useState(search);
  const [runs, setRuns] = useState<Run[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    api
      .getRunsPaged({
        page,
        pageSize: PAGE_SIZE,
        sort,
        order,
        status: status || undefined,
        storySlug: storySlug || undefined,
        search: search || undefined,
      })
      .then((res) => {
        if (!active) return;
        setRuns(res.runs);
        setTotal(res.total);
      })
      .catch((e) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : "Failed to load runs");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [api, page, sort, order, status, storySlug, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleSort = (col: RunListQuery["sort"]) => {
    if (sort === col) {
      updateParams({ order: order === "desc" ? "asc" : "desc", page: "1" });
    } else {
      updateParams({ sort: col ?? "createdAt", order: "desc", page: "1" });
    }
  };

  const sortIndicator = (col: RunListQuery["sort"]) => {
    if (sort !== col) return "";
    return order === "asc" ? " ↑" : " ↓";
  };

  const applySearch = () => {
    updateParams({ search: searchInput.trim() || null, page: "1" });
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Cluster" name="tasks" />

      {error && <AlertBanner variant="error">{error}</AlertBanner>}

      <Panel title="Filters">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase text-slate-500">
              Search
            </span>
            <div className="flex gap-2 mt-1">
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
                placeholder="title, story, id…"
                className={inputClass}
              />
              <button type="button" onClick={applySearch} className={btnSecondary}>
                Go
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase text-slate-500">
              Status
            </span>
            <select
              value={status}
              onChange={(e) =>
                updateParams({ status: e.target.value || null, page: "1" })
              }
              className={`${selectClass} mt-1`}
            >
              <option value="">all</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase text-slate-500">
              Story slug
            </span>
            <input
              type="text"
              value={storySlug}
              onChange={(e) =>
                updateParams({ storySlug: e.target.value || null, page: "1" })
              }
              placeholder="exact match"
              className={`${inputClass} mt-1`}
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() =>
                setSearchParams({}, { replace: true })
              }
              className={btnSecondary}
            >
              Clear filters
            </button>
          </div>
        </div>
      </Panel>

      <Panel title={`Runs (${total})`}>
        {loading ? (
          <p className="text-sm text-slate-500 font-mono">Loading runs…</p>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 -mb-4">
              <table className="min-w-full">
                <thead>
                  <tr>
                    {SORT_COLUMNS.map(({ key, label }) => (
                      <Th key={key}>
                        <button
                          type="button"
                          onClick={() => toggleSort(key)}
                          className="font-semibold hover:text-[#C9A260] text-left"
                        >
                          {label}
                          <span className="text-slate-400">{sortIndicator(key)}</span>
                        </button>
                      </Th>
                    ))}
                    <Th>Triggered by</Th>
                    <Th>PR</Th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-3 py-8 text-center text-sm text-slate-500"
                      >
                        No runs match the current filters.
                      </td>
                    </tr>
                  ) : (
                    runs.map((run) => (
                      <tr key={run.id} className="hover:bg-[#141210]">
                        <Td mono>{formatDate(run.createdAt)}</Td>
                        <Td mono>
                          <ResourceLink to={`/orchestration/runs/${run.id}`}>
                            {run.title || shortId(run.id)}
                          </ResourceLink>
                        </Td>
                        <Td mono>{run.storySlug}</Td>
                        <Td>
                          <StatusChip
                            status={run.status}
                            pulse={
                              run.status === "running" ||
                              run.status === "needs_rework"
                            }
                          />
                        </Td>
                        <Td mono>{run.iterations}</Td>
                        <Td mono>{formatDate(run.completedAt)}</Td>
                        <Td mono>
                          {run.triggeredBy || <span className="text-slate-400">—</span>}
                        </Td>
                        <Td>
                          {run.prUrl ? (
                            <a
                              href={run.prUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#C9A260] hover:underline text-sm font-mono"
                            >
                              pr ↗
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-4 mt-4 pt-3 border-t border-[#262420] text-sm">
              <span className="text-slate-500 font-mono">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => updateParams({ page: String(page - 1) })}
                  className={btnSecondary}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => updateParams({ page: String(page + 1) })}
                  className={btnSecondary}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

export default RunsPage;